/**
 * Configuration drawer for dynamic rate limit settings.
 *
 * Slides down from the top when triggered by the hamburger menu.
 * Allows editing V4 details rate limit settings.
 */

import { useEffect, useCallback } from 'react';
import { useConfigStore, hasChanges } from '../../stores/configStore';
import { Slider } from './Slider';

// =============================================================================
// CONFIG DRAWER
// =============================================================================

export function ConfigDrawer() {
  const isOpen = useConfigStore((state) => state.isDrawerOpen);
  const formValues = useConfigStore((state) => state.formValues);
  const serverConfig = useConfigStore((state) => state.serverConfig);
  const isLoading = useConfigStore((state) => state.isLoading);
  const isSaving = useConfigStore((state) => state.isSaving);
  const error = useConfigStore((state) => state.error);
  const source = useConfigStore((state) => state.source);
  const updatedAt = useConfigStore((state) => state.updatedAt);

  const closeDrawer = useConfigStore((state) => state.closeDrawer);
  const updateFormValue = useConfigStore((state) => state.updateFormValue);
  const saveConfig = useConfigStore((state) => state.saveConfig);
  const resetForm = useConfigStore((state) => state.resetForm);

  const hasUnsavedChanges = hasChanges(formValues, serverConfig);

  // Handle Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeDrawer();
      }
    },
    [isOpen, closeDrawer]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    await saveConfig();
  };

  const formatUpdatedAt = (iso: string | null) => {
    if (!iso) return 'Never';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0 z-40 bg-black/30 backdrop-blur-sm
          transition-opacity duration-200
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* Drawer Panel */}
      <div
        className={`
          fixed top-14 left-1/2 -translate-x-1/2 z-50
          w-full max-w-md
          bg-white dark:bg-gray-800 rounded-b-xl shadow-2xl
          transform transition-transform duration-200 ease-out
          ${isOpen ? 'translate-y-0' : '-translate-y-full'}
        `}
        role="dialog"
        aria-modal="true"
        aria-labelledby="config-drawer-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2
              id="config-drawer-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              Configuration
            </h2>
            {source && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Source: {source} • Updated: {formatUpdatedAt(updatedAt)}
              </p>
            )}
          </div>
          <button
            onClick={closeDrawer}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close configuration"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <svg
                className="animate-spin h-8 w-8 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          ) : formValues ? (
            <div className="space-y-6">
              {/* V4 Rate Limits Section */}
              <section>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  V4 Rate Limits
                </h3>
                <div className="space-y-4 pl-6">
                  <Slider
                    label="Details Rate"
                    value={formValues.rate_limits.v4_details_rate}
                    min={0.1}
                    max={10}
                    step={0.1}
                    unit=" req/s"
                    description="V4 journey details resolution rate"
                    onChange={(v) => updateFormValue('rate_limits', 'v4_details_rate', v)}
                  />

                  <Slider
                    label="Details Burst"
                    value={formValues.rate_limits.v4_details_burst}
                    min={1}
                    max={20}
                    step={1}
                    description="Maximum burst capacity for details"
                    onChange={(v) => updateFormValue('rate_limits', 'v4_details_burst', v)}
                  />
                </div>
              </section>

              {/* Error Display */}
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              Unable to load configuration
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
          <button
            onClick={resetForm}
            disabled={isLoading || isSaving || !hasUnsavedChanges}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            onClick={closeDrawer}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || isSaving || !hasUnsavedChanges}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            Save
          </button>
        </div>
      </div>
    </>
  );
}

export default ConfigDrawer;
